import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Spinner } from 'flowbite-react';
import { Drawer } from '../../../components/Modals';
import CustomButton from '../../../components/common/CustomButton';
import UnsavedChangesIndicator from '../../../components/common/UnsavedChangesIndicator';
import FormInput from '../../../components/common/FormInput';
import NumberInput from '../../../components/common/NumberInput';
import MultiSelect from '../../../components/common/MultiSelect';
import CustomTooltip from '../../../components/common/CustomTooltip';
import useOrganizationService from '../../../services/organization';
import useAzureService from '../../../services/azure';
import { useJwt } from '../../../helpers/common';
import { HiExclamationCircle, HiOutlineInformationCircle, HiCheck, HiOutlineUserAdd, HiOutlineX, HiChevronDown, HiArrowDown, HiDownload, HiHome } from 'react-icons/hi';
import { azureTeamSyncRepository } from '../../../services/repositories';
import { useResourcesService } from "../../../services";
import { setUsers, toggleUserSelection } from '../../../redux/slices/manageUsers';
import SyncUsersModal from './SyncUsersModal';
import { IntegrationActiveType } from '../../Settings/types';
import useIntegrationStatus from '../../../hooks/useIntegrationStatus';

interface CreateTeamModalProps {
  isVisible: boolean;
  onClose: () => void;
  editingTeam?: {
    id: number;
    name: string;
    description?: string;
    members: Array<{ id: number; name: string; email: string }>;
    projects?: { project_id: string; project_name: string }[];
    imported?: boolean;
    story_points?: number | null;
    original_estimate?: number | null;
  } | null;
  onRefresh?: () => void;
  onImportUsersClick?: (user?: any) => void;
}

const CreateTeamModal: React.FC<CreateTeamModalProps> = ({
  isVisible,
  onClose,
  editingTeam = null,
  onRefresh,
  onImportUsersClick
}) => {
  const [formData, setFormData] = useState({
    team_name: '',
    team_description: '',
    story_points: null as number | null,
    original_estimate: null as number | null,
    team_members: [] as number[]
  });
  const [selectedMembers, setSelectedMembers] = useState<any[]>([]);
  const [teamNameError, setTeamNameError] = useState<string>('');
  // Add state for copied project id
  const [copiedProjectId, setCopiedProjectId] = useState<string | null>(null);
  // State for showing resync message after story points or original estimate save
  const [showStoryPointsResyncMessage, setShowStoryPointsResyncMessage] = useState(false);
  const [showOriginalEstimateResyncMessage, setShowOriginalEstimateResyncMessage] = useState(false);
  
  // State for import modal
  const [showImportConfirm, setShowImportConfirm] = useState(false);

  // Track saved/API values to determine when to show info message
  const [savedStoryPoints, setSavedStoryPoints] = useState<number | null>(null);
  const [savedOriginalEstimate, setSavedOriginalEstimate] = useState<number | null>(null);
  
  // Ref for scrolling to sync differences section
  const syncDifferencesRef = useRef<HTMLDivElement>(null);

  const isEditing = !!editingTeam;

  const { createTeam, updateTeam } = useOrganizationService();
  const { fetchManagerResources } = useResourcesService();
  const { acceptAzureTeamMembers, rejectAzureTeamMembers } = useAzureService();
  const { teamsLoading, teams, orgDetails } = useSelector((state: any) => state.organization);
  const { data: resourcesData } = useSelector((state: any) => state.resources);
  const { syncDifferences, syncActionsLoading, syncDifferencesLoading } = useSelector((state: any) => state.azureTeams);
  const dispatch = useDispatch();
  const { isIntegrationActive } = useIntegrationStatus();
  const JWTdata: any = useJwt();
  const org_id = JWTdata.org_id;
  const user_id = JWTdata.user_id;
  const navigate = useNavigate();

  // Check if team name already exists
  const checkTeamNameExists = useCallback((name: string) => {
    if (!name.trim()) {
      setTeamNameError('');
      return false;
    }

    const normalizedName = name.trim().toLowerCase();
    const existingTeam = teams?.find((team: any) =>
      team.name.trim().toLowerCase() === normalizedName &&
      (!isEditing || team.id !== editingTeam?.id)
    );

    if (existingTeam && !isEditing) {
      setTeamNameError('A team with this name already exists');
      return true;
    }

    setTeamNameError('');
    return false;
  }, [teams, isEditing, editingTeam]);

  // Populate form when editing
  useEffect(() => {
    if (editingTeam) {
      const storyPts = (editingTeam as any).story_points || null;
      const origEst = (editingTeam as any).original_estimate || null;

      setFormData({
        team_name: editingTeam.name,
        team_description: editingTeam.description || '',
        story_points: storyPts,
        original_estimate: origEst,
        team_members: editingTeam.members.map(member => member.id)
      });

      // Track saved values from API
      setSavedStoryPoints(storyPts);
      setSavedOriginalEstimate(origEst);

      // Set selected members for multiselect
      const memberOptions = editingTeam.members.map(member => ({
        value: member.id,
        label: `${member.name} (${member.email})`
      }));
      setSelectedMembers(memberOptions);
    } else {
      // Reset form for create mode
      setFormData({ team_name: '', team_description: '', story_points: null, original_estimate: null, team_members: [] as number[] });
      setSelectedMembers([]);
      setSavedStoryPoints(null);
      setSavedOriginalEstimate(null);
    }
    // Reset error when switching between create/edit modes
    setTeamNameError('');
  }, [editingTeam]);

  const currentTeamData = useMemo(() => {
    if (!isEditing || !editingTeam || !teams) return null;
    return teams.find((team: any) => team.id === editingTeam.id);
  }, [isEditing, editingTeam, teams]);

  const hasUnsavedChanges = useMemo(() => {
    if (!isEditing || !editingTeam) {
      return false;
    }

    const teamDataToCompare = currentTeamData || editingTeam;

    const normalizeString = (val: any) => {
      if (val === null || val === undefined) return '';
      return String(val).trim();
    };

    const currentMembers = (selectedMembers || []).map((m: any) => m.value).sort();
    const initialMembers = (teamDataToCompare.members || []).map((m: any) => m.id).sort();

    return (
      normalizeString(formData.team_name) !== normalizeString(teamDataToCompare.name || '') ||
      normalizeString(formData.team_name) !== normalizeString(teamDataToCompare.name || '') ||
      normalizeString(formData.team_description) !== normalizeString(teamDataToCompare.description || '') ||
      formData.story_points !== ((teamDataToCompare as any).story_points || null) ||
      formData.original_estimate !== ((teamDataToCompare as any).original_estimate || null) ||
      JSON.stringify(currentMembers) !== JSON.stringify(initialMembers)
    );
  }, [isEditing, editingTeam, currentTeamData, formData.team_name, formData.team_description, formData.story_points, formData.original_estimate, selectedMembers]);

  // Memoized options for resources multiselect
  const resourceOptions = useMemo(() => {
    if (!resourcesData || resourcesData.length === 0) return [];

    return [...resourcesData]
      // Only include resources with active invite status (exclude pending/expired)
      .filter((resource: any) =>
        resource?.invite_status !== 'pending' &&
        resource?.invite_status !== 'expired' &&
        !resource?.is_deleted
      ).sort((a: any, b: any) => a.name?.toLowerCase().localeCompare(b.name?.toLowerCase()))
      .map((resource: any) => ({
        value: resource.id,
        label: resource.email ? `${resource.name} (${resource.email})` : resource.name,
      }));
  }, [resourcesData]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Check for duplicate team name on change
    if (name === 'team_name') {
      checkTeamNameExists(value);
    }
  }, [checkTeamNameExists]);

  const handleMembersChange = useCallback((value: any) => {
    setSelectedMembers(value);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!formData.team_name.trim()) {
      setTeamNameError('Team name is required');
      return;
    }

    // Check for duplicate team name before submission
    if (checkTeamNameExists(formData.team_name)) {
      return;
    }

    if (!org_id) {
      console.error('Organization ID not found');
      return;
    }

    // Check if story points or original estimate changed before saving
    const storyPointsChanged = isEditing &&
      editingTeam?.story_points !== undefined &&
      formData.story_points !== null &&
      formData.story_points !== editingTeam.story_points;

    const originalEstimateChanged = isEditing &&
      editingTeam?.original_estimate !== undefined &&
      formData.original_estimate !== null &&
      formData.original_estimate !== editingTeam.original_estimate;

    try {
      if (isEditing) {
        // Handle editing mode
        if (!editingTeam) return;

        // Use updateTeam API with team_id in payload
        const teamDataWithMembers = {
          team_id: editingTeam.id,
          team_name: formData.team_name,
          team_description: formData.team_description,
          story_points: formData.story_points,
          original_estimate: formData.original_estimate,
          team_members: selectedMembers.map(member => member.value)
        };

        await updateTeam(org_id, teamDataWithMembers);
      } else {
        // Handle create mode
        const teamDataWithMembers = {
          ...formData,
          team_members: selectedMembers.map(member => member.value)
        };

        await createTeam(org_id, teamDataWithMembers);
      }

      // Trigger refresh of teams data
      if (onRefresh) {
        onRefresh();
      }

      // If story points or original estimate changed, show resync message and keep modal open
      if (storyPointsChanged || originalEstimateChanged) {
        setShowStoryPointsResyncMessage(storyPointsChanged);
        setShowOriginalEstimateResyncMessage(originalEstimateChanged);
        // Update saved values after successful save
        setSavedStoryPoints(formData.story_points);
        setSavedOriginalEstimate(formData.original_estimate);
      } else {
        // Reset form and close only if neither changed
        setFormData({ team_name: '', team_description: '', story_points: null, original_estimate: null, team_members: [] as number[] });
        setSelectedMembers([]);
        setTeamNameError('');
        setSavedStoryPoints(null);
        setSavedOriginalEstimate(null);
        onClose();
      }
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} team:`, error);
    }
  }, [formData, selectedMembers, org_id, isEditing, editingTeam, createTeam, updateTeam, onClose, onRefresh, checkTeamNameExists]);

  const handleClose = useCallback(() => {
    setFormData({ team_name: '', team_description: '', story_points: null, original_estimate: null, team_members: [] as number[] });
    setSelectedMembers([]);
    setTeamNameError('');
    // Clear resync messages
    setShowStoryPointsResyncMessage(false);
    setShowOriginalEstimateResyncMessage(false);
    // Clear saved values
    setSavedStoryPoints(null);
    setSavedOriginalEstimate(null);
    onClose();
  }, [onClose]);

  // Disable submit button when there's an error or required fields are missing
  const isSubmitDisabled = teamsLoading || !formData.team_name.trim() || !!teamNameError;

  const handleCopyProjectId = useCallback((projectId: string) => {
    navigator.clipboard.writeText(projectId);
    setCopiedProjectId(projectId);
    setTimeout(() => setCopiedProjectId(null), 1200);
  }, []);

  // Get sync differences for current editing team
  const teamSyncDifference = useMemo(() => {
    if (!isEditing || !editingTeam || !syncDifferences) return null;
    return syncDifferences.find((diff: any) => diff.id === editingTeam.id);
  }, [isEditing, editingTeam, syncDifferences]);

  const { importedMembers, nonImportedMembers } = useMemo(() => {
    if (!teamSyncDifference || !teamSyncDifference.members) {
      return { importedMembers: [], nonImportedMembers: [] };
    }

    const imported = teamSyncDifference.members.filter((member: any) => member.isImported !== false);
    const nonImported = teamSyncDifference.members.filter((member: any) => member.isImported === false);

    return { importedMembers: imported, nonImportedMembers: nonImported };
  }, [teamSyncDifference]);

  // Handle accepting individual member
  const handleAcceptMember = useCallback(async (member: any) => {
    if (!editingTeam) return;

    try {
      await acceptAzureTeamMembers(org_id, editingTeam.id, [member]);

      // Clear cache after successful operation
      if (org_id && user_id) {
        await azureTeamSyncRepository.clearCachedData(org_id, user_id);
      }

      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error accepting member:', error);
    }
  }, [org_id, user_id, editingTeam, acceptAzureTeamMembers, onRefresh]);

  // Handle rejecting individual member
  const handleRejectMember = useCallback(async (member: any) => {
    if (!org_id || !editingTeam) return;

    try {
      await rejectAzureTeamMembers(org_id, String(editingTeam.id), [member]);

      // Clear cache after successful operation
      if (org_id && user_id) {
        await azureTeamSyncRepository.clearCachedData(org_id, user_id);
      }

      await fetchManagerResources(org_id);
    } catch (error) {
      console.error('Error rejecting member:', error);
    }
  }, [org_id, user_id, editingTeam, rejectAzureTeamMembers]);

  // Handle accepting ALL Azure team member changes
  const handleAcceptAllMembers = useCallback(async () => {
    if (!editingTeam || !importedMembers || importedMembers.length === 0) return;

    try {
      await acceptAzureTeamMembers(org_id, editingTeam.id, importedMembers);

      // Clear cache after successful operation
      if (org_id && user_id) {
        await azureTeamSyncRepository.clearCachedData(org_id, user_id);
      }

      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error accepting all member changes:', error);
    }
  }, [org_id, user_id, editingTeam, importedMembers, acceptAzureTeamMembers, onRefresh]);

  // Handle rejecting ALL Azure team member changes
  const handleRejectAllMembers = useCallback(async () => {
    if (!org_id || !editingTeam || !importedMembers || importedMembers.length === 0) return;

    try {
      await rejectAzureTeamMembers(org_id, String(editingTeam.id), importedMembers);

      // Clear cache after successful operation
      if (org_id && user_id) {
        await azureTeamSyncRepository.clearCachedData(org_id, user_id);
      }
    } catch (error) {
      console.error('Error rejecting all member changes:', error);
    }
  }, [org_id, user_id, editingTeam, importedMembers, rejectAzureTeamMembers]);

  // Handle direct import of a user via modal
  const handleImportUser = useCallback((member: any) => {
    const memberId = String(member.id);
    
    // Construct user object for Redux state
    // We start with "selected: false" so that we can toggle it to true,
    // creating a difference between initialUsers and users states which SyncUsersModal relies on.
    const userToImport = {
      id: memberId,
      display_name: member.name,
      email: member.email,
      present_in_org: false,
      selected: false, // Init as false
      is_deleted: false
    };

    // Set users in Redux
    // This sets BOTH users and initialUsers to have selected: false
    dispatch(setUsers({
      users: [userToImport],
      summary: {
        total_azure_users: 0,
        currently_in_org: 0,
        deleted_users: 0
      }
    }));

    // Now toggle selection to true
    // This updates users (selected: true) but initialUsers remains (selected: false)
    dispatch(toggleUserSelection(memberId));

    setShowImportConfirm(true);
  }, [dispatch]);

  // Scroll to sync differences section
  const scrollToSyncDifferences = useCallback(() => {
    if (syncDifferencesRef.current) {
      syncDifferencesRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, []);

  // This effect runs when a member is successfully accepted via the API.
  // It adds the new member to the multiselect dropdown.
  useEffect(() => {
    if (!editingTeam) return;

    // Get the initial list of members for the team from syncDifferences before any changes
    const originalSyncData = (syncDifferences??[]).find((diff: any) => diff.id === editingTeam.id);
    if (!originalSyncData) return;

    // Find members that were in the original sync list but are no longer in the current sync list for this team
    const acceptedMembers = (originalSyncData?.members??[]).filter((originalMember: any) =>
      !teamSyncDifference?.members.some((currentMember: any) => currentMember.id === originalMember.id)
    );

    if (acceptedMembers.length > 0) {
      const newMemberOptions = acceptedMembers.map((member: any) => ({
        value: parseInt(member.id, 10), // Assuming member.id is a string that needs to be an integer
        label: `${member.name} (${member.email})`
      }));

      setSelectedMembers(prev => {
        const existingMemberIds = new Set(prev.map(m => m.value));
        const uniqueNewMembers = newMemberOptions.filter((opt: any) => !existingMemberIds.has(opt.value));
        return [...prev, ...uniqueNewMembers];
      });
    }
  }, [teamSyncDifference, editingTeam, syncDifferences]);

  // This effect will react to changes in the global `teams` store
  // and update the selected members if the currently editing team is updated.
  useEffect(() => {
    if (isEditing && editingTeam && teams) {
      const updatedTeamData = teams.find((team: any) => team.id === editingTeam.id);
      if (updatedTeamData) {
        const memberOptions = updatedTeamData.members.map((member: any) => ({
          value: member.id,
          label: `${member.name} (${member.email})`
        }));
        setSelectedMembers(memberOptions);
      }
    }
  }, [teams, editingTeam, isEditing]);

  return (
    <Drawer isOpen={isVisible} onClose={handleClose} position="right">
      <div className="h-screen flex flex-col bg-light-surface dark:bg-tertiary-dark">
        {/* Header */}
        <div className="flex-shrink-0 flex rounded-b-2xl shadow-md justify-between items-center bg-light-surfacePrimary dark:bg-tertiary-dark px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-light-text-primary dark:text-white">
              {isEditing ? "Edit Team" : "Create New Team"}
            </h2>
            <p className="text-xs text-light-text-secondary dark:text-white/70 mt-0.5">
              {isEditing
                ? "Update team and people"
                : "Add a new team to your organization"}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {teamsLoading ? (
            <div className="flex justify-center items-center h-full">
              <Spinner />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Story Points / Original Estimate Resync Message - Show at top after save */}
              {(showStoryPointsResyncMessage ||
                showOriginalEstimateResyncMessage) && (
                <div className="mb-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 animate-in fade-in slide-in-from-top-2 duration-200 overflow-hidden">
                  {/* Success message section */}
                  <div className="p-3 flex items-start gap-2.5">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-green-600 dark:text-green-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-800 dark:text-green-200">
                        {showStoryPointsResyncMessage &&
                        showOriginalEstimateResyncMessage
                          ? "Story points and original estimate updated successfully!"
                          : showStoryPointsResyncMessage
                            ? "Story points updated successfully!"
                            : "Original estimate updated successfully!"}
                      </p>
                      <p className="mt-1 text-xs text-green-600 dark:text-green-400">
                        This change will reflect to your project during the next
                        auto-sync, which may take up to 24 hours.
                      </p>
                    </div>
                  </div>
                  {/* Divider */}
                  <div className="border-t border-green-200 dark:border-green-800/50" />
                  {/* Action section */}
                  <div className="p-3 bg-green-100/50 dark:bg-green-900/30 flex items-center justify-between gap-3">
                    <p className="text-xs text-green-700 dark:text-green-300">
                      Need changes immediately?
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          onClose();
                          navigate('/#importproject=azure&manageProjects=true');
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors shadow-sm"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                        Resync Now
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowStoryPointsResyncMessage(false);
                          setShowOriginalEstimateResyncMessage(false);
                          setFormData({
                            team_name: "",
                            team_description: "",
                            story_points: null,
                            original_estimate: null,
                            team_members: [] as number[],
                          });
                          setSelectedMembers([]);
                          setTeamNameError("");
                          onClose();
                        }}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-800/40 hover:bg-green-200 dark:hover:bg-green-800/60 rounded-md transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Subtle Floating Changes Indicator */}
              {isEditing &&
                teamSyncDifference &&
                teamSyncDifference.members &&
                teamSyncDifference.members.length > 0 && (
                  <div className="flex justify-center mb-4">
                    <button
                      onClick={scrollToSyncDifferences}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-full text-xs font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all duration-200 shadow-sm group"
                    >
                      <span>
                        {importedMembers.length > 0 &&
                          `${importedMembers.length} new people to be added`}
                        {importedMembers.length > 0 &&
                          nonImportedMembers.length > 0 &&
                          " • "}
                        {nonImportedMembers.length > 0 &&
                          `${nonImportedMembers.length} need${nonImportedMembers.length > 1 ? "" : "s"} import`}
                      </span>
                      <HiArrowDown className="w-3 h-3 animate-bounce group-hover:animate-pulse" />
                    </button>
                  </div>
                )}

              {/* Team Info Bar - Only show when editing */}
              {isEditing && editingTeam && (
                <div className="bg-white dark:bg-[#1a2233] rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-md">
                  <div className="flex items-center justify-between">
                    {/* Left - Team Avatar and Info */}
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 dark:from-blue-500 dark:to-blue-700 flex items-center justify-center text-white font-semibold text-sm shadow-sm">
                        {editingTeam.name.substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                            {editingTeam.name}
                          </h4>
                          {/* Source badge - Imported or Cadence */}
                          {editingTeam.imported ? (
                            <div className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium border border-blue-400 rounded-md text-blue-600 bg-blue-50 dark:bg-blue-950 dark:border-blue-500 dark:text-blue-400 shadow-sm">
                              <HiDownload className="w-3 h-3" />
                              <span>Imported</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium border border-emerald-400 rounded-md text-emerald-600 bg-emerald-50 dark:bg-emerald-950 dark:border-emerald-500 dark:text-emerald-400 shadow-sm">
                              <HiHome className="w-3 h-3" />
                              <span>Cadence</span>
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {editingTeam.description && (
                            <>
                              <span className="">
                                {editingTeam.description.length > 30
                                  ? `${editingTeam.description.substring(0, 30)}...`
                                  : editingTeam.description}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right - Member Count */}
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <div className="text-sm font-bold text-gray-900 dark:text-white">
                          {selectedMembers?.length || 0}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          People
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Team Projects Display (Moved here) */}
                  {editingTeam.projects && editingTeam.projects.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wider">
                        Assigned Projects
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {editingTeam.projects.map((project) => (
                          <span
                            key={project.project_id}
                            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 border border-blue-200 dark:border-blue-800/30"
                            title={project.project_name}
                          >
                            {project.project_name}
                            <span className="ml-1.5 relative group">
                              {copiedProjectId === project.project_id ? (
                                <HiCheck
                                  className="w-3 h-3 text-green-500"
                                  title="Copied!"
                                />
                              ) : (
                                <HiOutlineInformationCircle
                                  className="w-3 h-3 text-blue-400 cursor-pointer"
                                  onClick={() =>
                                    handleCopyProjectId(project.project_id)
                                  }
                                  title={`ID: ${project.project_id} (Click to copy)`}
                                />
                              )}
                            </span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div>
                <FormInput
                  id="team_name"
                  name="team_name"
                  label="Team Name"
                  value={formData.team_name}
                  onChange={handleInputChange}
                  placeholder="Enter team name"
                  required
                  showAsterisk
                />
                {teamNameError && (
                  <div className="-mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                    <HiExclamationCircle className="w-4 h-4" />
                    <span>{teamNameError}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-light-text-primary dark:text-white mb-2">
                  Team Description
                </label>
                <textarea
                  id="team_description"
                  name="team_description"
                  value={formData.team_description}
                  onChange={handleInputChange}
                  placeholder="Enter team description"
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                           focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                           bg-[#f9fafb] dark:bg-[#1e283a] text-gray-900 dark:text-white
                           placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>

              {isEditing && editingTeam?.imported && (
                <div>
                  {/* Grid layout for Story Points and Original Estimate - side by side */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Average Story Points */}
                    <div>
                      <div className="flex items-center gap-1 mb-1">
                        <label
                          htmlFor="story_points"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Average Story Points
                        </label>
                        <CustomTooltip
                          title={`This value (${formData.story_points ? `${formData.story_points} Story Points` : `Default: ${orgDetails?.story_points || 1} Story Points`}) is used for capacity planning and workload estimation when specific story points are not assigned to user-stories / bugs / spikes.`}
                          placement="top"
                        >
                          <div className="cursor-help text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300">
                            <HiOutlineInformationCircle className="w-4 h-4" />
                          </div>
                        </CustomTooltip>
                      </div>
                      <NumberInput
                        id="story_points"
                        name="story_points"
                        value={formData.story_points ?? undefined}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            story_points: val === "" ? null : Number(val),
                          }));
                        }}
                        placeholder="Enter story points"
                        className="w-full"
                        min={1}
                      />
                    </div>

                    {/* Average Original Estimate */}
                    <div>
                      <div className="flex items-center gap-1 mb-1">
                        <label
                          htmlFor="original_estimate"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Average Original Estimate (hours)
                        </label>
                        <CustomTooltip
                          title={`This value (${formData.original_estimate ? `${formData.original_estimate} hours` : `Default: ${orgDetails?.original_estimate || 8} hours`}) is used for capacity planning and workload estimation when specific original estimate is not assigned to Tasks in Azure DevOps.`}
                          placement="top"
                        >
                          <div className="cursor-help text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300">
                            <HiOutlineInformationCircle className="w-4 h-4" />
                          </div>
                        </CustomTooltip>
                      </div>
                      <NumberInput
                        id="original_estimate"
                        name="original_estimate"
                        value={formData.original_estimate ?? undefined}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            original_estimate: val === "" ? null : Number(val),
                          }));
                        }}
                        placeholder="Enter hours"
                        className="w-full"
                        min={1}
                      />
                    </div>
                  </div>

                  {/* Full width info message - shows when either SAVED value is empty (not local form values) */}
                  {(savedStoryPoints === null ||
                    savedOriginalEstimate === null) &&
                    !showStoryPointsResyncMessage &&
                    !showOriginalEstimateResyncMessage && (
                      <div className="mt-2 flex items-start gap-2 p-2 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30">
                        <HiOutlineInformationCircle className="w-4 h-4 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-gray-600 dark:text-gray-300">
                          <p className="mb-1">
                            Leave empty to use organization defaults:{" "}
                            <span className="font-semibold text-gray-900 dark:text-white">
                              {orgDetails?.story_points || 1} Story Points
                            </span>{" "}
                            and{" "}
                            <span className="font-semibold text-gray-900 dark:text-white">
                              {orgDetails?.original_estimate || 8} hours
                            </span>
                            .
                          </p>
                          <button
                            onClick={() => {
                              onClose();
                              navigate("/settings#OrgSettings");
                            }}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline font-medium inline-flex items-center gap-1 transition-colors"
                          >
                            Configure organization defaults &rarr;
                          </button>
                        </div>
                      </div>
                    )}
                </div>
              )}

              <MultiSelect
                id="team_members"
                name="team_members"
                label="Team Members"
                value={selectedMembers}
                onChange={handleMembersChange}
                options={resourceOptions}
                placeholder="Select team members"
                showAsterisk={false}
              />

              {syncDifferencesLoading ? (
                <div className="flex justify-center items-center py-4">
                  <svg
                    className="animate-spin h-10 w-10 text-blue-600 dark:text-blue-400"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>{" "}
                </div>
              ) : (
                isEditing &&
                teamSyncDifference &&
                teamSyncDifference.members &&
                teamSyncDifference.members.length > 0 && (
                  <div ref={syncDifferencesRef}>
                    {importedMembers.length > 0 && (
                      <div className="flex items-start gap-3 mb-6">
                        <div className="flex-shrink-0">
                          <HiOutlineUserAdd className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-light-text-primary dark:text-white mb-1">
                            New People Found in Azure
                          </h4>
                          <p className="text-sm text-light-text-secondary dark:text-white/70 mb-3">
                            We found {importedMembers.length} new people in
                            Azure. Accept them to add them to this team, or
                            ignore to keep the team as is.
                          </p>

                          {/* Bulk Action Buttons */}
                          <div className="flex gap-2 mb-4">
                            <button
                              onClick={() => {
                                handleAcceptAllMembers();
                              }}
                              disabled={syncActionsLoading}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <HiCheck className="w-4 h-4 mr-1" />
                              Accept All
                            </button>
                            <button
                              onClick={() => {
                                handleRejectAllMembers();
                              }}
                              disabled={syncActionsLoading}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <HiOutlineX className="w-4 h-4 mr-1" />
                              Ignore All
                            </button>
                          </div>

                          {/* Member List */}
                          <div className="space-y-2">
                            {importedMembers.map(
                              (member: any, index: number) => (
                                <div
                                  key={member.id || index}
                                  className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-md p-2 border border-gray-200 dark:border-gray-700"
                                >
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-semibold">
                                    {member.name
                                      ? member.name
                                          .substring(0, 2)
                                          .toUpperCase()
                                      : "NA"}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                      {member.name || "Unknown Name"}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                      {member.email || "No email provided"}
                                    </div>
                                  </div>
                                  <div className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded-full mr-2">
                                    New
                                  </div>
                                  {/* Individual Action Buttons */}
                                  <div className="flex gap-1">
                                    <button
                                      onClick={() => handleAcceptMember(member)}
                                      disabled={syncActionsLoading}
                                      className="inline-flex items-center p-1.5 text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                      title="Accept this person"
                                    >
                                      <HiCheck className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={() => handleRejectMember(member)}
                                      disabled={syncActionsLoading}
                                      className="inline-flex items-center p-1.5 text-xs font-medium rounded-md text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                      title="Ignore this person"
                                    >
                                      <HiOutlineX className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {nonImportedMembers.length > 0 && (
                      <div className="flex items-start gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex-shrink-0">
                          <HiOutlineInformationCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-light-text-primary dark:text-white mb-1">
                            People Need to be Imported First
                          </h4>
                          <p className="text-sm text-light-text-secondary dark:text-white/70 mb-3">
                            We found {nonImportedMembers.length} user
                            {nonImportedMembers.length > 1 ? "s" : ""} that need
                            to be imported to Cadence Pro before they can be
                            added to this team.
                          </p>

                          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3 mb-4">
                            <p className="text-sm text-blue-800 dark:text-blue-300">
                              <span className="font-medium">
                                To import these people:
                              </span>{" "}
                              Go to the{" "}
                              <span className="font-semibold">People</span> tab
                              and click the{" "}
                              <span className="font-semibold">
                                Import People
                              </span>{" "}
                              button to open the import modal. Once imported,
                              you can return here to accept them and add them to
                              this team.
                            </p>
                          </div>

                          <div className="space-y-2">
                            {nonImportedMembers.map(
                              (member: any, index: number) => (
                                <div
                                  key={member.id || index}
                                  className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800/50 rounded-md p-2 border border-gray-200 dark:border-gray-700"
                                >
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center text-white text-xs font-semibold">
                                    {member.name
                                      ? member.name
                                          .substring(0, 2)
                                          .toUpperCase()
                                      : "NA"}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                      {member.name || "Unknown Name"}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                      {member.email || "No email provided"}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleImportUser(member)}
                                    className="text-xs px-2 py-1 rounded-full cursor-pointer transition-colors flex items-center gap-1 bg-yellow-100 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:hover:bg-yellow-900/50 text-yellow-700 dark:text-yellow-400"
                                    title="Click to import this user"
                                  >
                                    <span>Not Imported, Click to Import</span>
                                  </button>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 rounded-t-md flex justify-between items-center border-t border-gray-500 dark:border-hover-dark bg-light-surfacePrimary dark:bg-tertiary-dark px-6 py-4">
          <CustomButton variant="cancel" onClick={handleClose}>
            Cancel
          </CustomButton>
          <div className="ml-auto flex items-center gap-3">
            {isEditing && (
              <UnsavedChangesIndicator hasUnsavedChanges={hasUnsavedChanges} />
            )}
            <CustomButton
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              isLoading={teamsLoading}
            >
              {isEditing ? "Update Team" : "Create Team"}
            </CustomButton>
          </div>
        </div>
      </div>
      {showImportConfirm && (
        <SyncUsersModal
          activeTab={
            isIntegrationActive("azure")
              ? IntegrationActiveType.AZURE
              : IntegrationActiveType.JIRA
          }
          setShowImportConfirm={setShowImportConfirm}
          hasChangesToImport={true}
        />
      )}
    </Drawer>
  );
};

export default CreateTeamModal; 